import { useState, useEffect } from 'react'
import axios from 'axios'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PlayerSorter from './PlayerSorter'
import Table from 'react-bootstrap/Table'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import loginUser from '../services/login'
import RangeSlider from './RangeSlider'
import FindPlayer from './FindPlayer'
import {
  setAllPlayersFilters,
  setAllPlayersResults,
} from '../reducers/allPlayersReducer'

const AllPlayers = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { filters, results: filtered } = useSelector(state => state.allPlayers)
  const [loading, setLoading] = useState(false)
  const user = useSelector(state => state.user)

  // TIETOTURVA: Tarkista Staff-oikeudet
  const hasStaffAccess = user && user.staff && user.staff.length > 0

  useEffect(() => {
    // Odotetaan että user.staff on määritelty (ei undefined) ennen tarkistusta
    // Jos user on olemassa JA staff on määritelty JA ei ole Staff-oikeuksia, ohjaa pois
    if (user && user.staff !== undefined && !hasStaffAccess) {
      navigate('/')
    }
  }, [user, hasStaffAccess, navigate])

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true)
        // Lataa käyttäjätiedot
        await loginUser(dispatch)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching initial data', error)
        setLoading(false)
      }
    }

    fetchInitialData()
  }, [dispatch])

  const handleFilterChange = (name, value) => {
    if (name === 'age') {
      dispatch(
        setAllPlayersFilters({
          ...filters,
          age: {
            ...filters.age,
            ...value,
          },
        })
      )
    } else {
      dispatch(
        setAllPlayersFilters({
          ...filters,
          [name]: value,
        })
      )
    }
  }

  const applyFilters = async () => {
    setLoading(true) // Aloitetaan suodattaminen
    try {
      // Hae aktiivisen maajoukkueen LeagueID nationeista
      let leagueId = null
      if (user.activeNationalTeam) {
        try {
          const nationResponse = await axios.get(
            `/api/nations/team/${user.activeNationalTeam}`
          )
          if (nationResponse.data && nationResponse.data.LeagueID) {
            leagueId = nationResponse.data.LeagueID
          }
        } catch (err) {
          console.error('Error fetching nation for filtering:', err)
        }
      }

      const response = await axios.post(
        '/api/players/filter/' + user.oauth_token,
        { ...filters, leagueId, ageMargin: 28 }
      )
      dispatch(setAllPlayersResults(response.data))
      setLoading(false) // Suodatus valmis
    } catch (error) {
      console.error('Error fetching filtered players', error)
      setLoading(false)
    }
  }

  // TIETOTURVA: Näytä viesti jos ei Staff-oikeuksia
  // Mutta vain jos user.staff on määritelty (ei undefined)
  if (user && user.staff !== undefined && !hasStaffAccess) {
    return (
      <Card bg="dark" text="light" className="mt-3">
        <Card.Body className="text-center">
          <h4>Ei käyttöoikeutta</h4>
          <p>Tämä sivu on tarkoitettu vain maajoukkueen Staff-jäsenille.</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Palaa etusivulle
          </Button>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Table striped bordered variant="dark" size="sm">
        <tbody>
          <tr>
            <RangeSlider
              name="keeper"
              value={filters.keeper}
              onChange={value => handleFilterChange('keeper', value)}
            />
            <RangeSlider
              name="defending"
              value={filters.defending}
              onChange={value => handleFilterChange('defending', value)}
            />
          </tr>
          <tr>
            <RangeSlider
              name="playmaking"
              value={filters.playmaking}
              onChange={value => handleFilterChange('playmaking', value)}
            />
            <RangeSlider
              name="wing"
              value={filters.wing}
              onChange={value => handleFilterChange('wing', value)}
            />
          </tr>
          <tr>
            <RangeSlider
              name="passing"
              value={filters.passing}
              onChange={value => handleFilterChange('passing', value)}
            />
            <RangeSlider
              name="scoring"
              value={filters.scoring}
              onChange={value => handleFilterChange('scoring', value)}
            />
          </tr>
          <tr>
            <RangeSlider
              name="setpieces"
              value={filters.setpieces}
              onChange={value => handleFilterChange('setpieces', value)}
            />
            <RangeSlider
              name="HTMS"
              value={filters.HTMS}
              onChange={value => handleFilterChange('HTMS', value)}
              min={0}
              max={3000}
              step={20}
            />
          </tr>
          <tr>
            <RangeSlider
              name="years"
              value={filters.age.years}
              onChange={value => handleFilterChange('age', { years: value })}
              min={17}
              max={36}
            />
            <RangeSlider
              name="days"
              value={filters.age.days}
              onChange={value => handleFilterChange('age', { days: value })}
              min={0}
              max={111}
            />
          </tr>
          <tr>
            <td colSpan={2}>
              <button onClick={applyFilters}>Filter</button>
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              <FindPlayer />
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Näytetään spinneri, jos lataus on käynnissä */}
      {loading ? (
        <Table striped bordered hover variant="dark" size="sm">
          <Spinner animation="border" />
        </Table>
      ) : (
        // PlayerSorter käytössä, jos data on ladattu
        filtered.length > 0 && <PlayerSorter players={filtered} />
      )}
    </>
  )
}

export default AllPlayers
